import { useEffect } from 'react';
import { GetStaticPaths, GetStaticProps } from 'next';
import { PHASE_PRODUCTION_BUILD } from 'next/constants';
import {
  SitecoreProvider,
  ComponentPropsContext,
  StaticPath,
  SiteInfo,
} from '@sitecore-content-sdk/nextjs';
import { handleEditorFastRefresh } from '@sitecore-content-sdk/nextjs/utils';
import { SitecorePageProps } from 'lib/page-props';
import { sitecorePagePropsFactory } from 'lib/page-props-factory';
import components from '.sitecore/component-map';
import sites from '.sitecore/sites.json';
import scConfig from 'sitecore.config';
import client from 'lib/sitecore-client';
import HandleMockError, { getMockError } from 'components/helpers/ErrorHandling/HandleMockError';
import Layout from 'src/Layout';
import NotFound from 'src/NotFound';
import { pathExtractor } from 'lib/extract-path';

// Stable fallback so `componentProps || {}` never creates a new object reference
const EMPTY_COMPONENT_PROPS = {};

const SitecorePage = ({
  notFound,
  componentProps,
  page,
  mockError,
  headLinks,
}: SitecorePageProps): JSX.Element => {
  useEffect(() => {
    // Only run inside the Sitecore Experience Editor (legacy chromes mode).
    // ChromeManager is injected by the editor frame and is never present in normal
    // browser runtime — without this guard, the reload fires on every mount and
    // causes an infinite page refresh loop.
    if (
      (window as Window & { Sitecore?: { PageModes?: { ChromeManager?: unknown } } }).Sitecore
        ?.PageModes?.ChromeManager
    ) {
      handleEditorFastRefresh();
    }
  }, []);
  if (mockError) {
    return <HandleMockError {...mockError} />;
  }

  if (notFound || !page) {
    // Shouldn't hit this (as long as 'notFound' is being returned below), but just to be safe
    return <NotFound />;
  }

  return (
    <ComponentPropsContext value={componentProps ?? EMPTY_COMPONENT_PROPS}>
      <SitecoreProvider componentMap={components} api={scConfig.api} page={page}>
        <Layout page={page} headLinks={headLinks} />
      </SitecoreProvider>
    </ComponentPropsContext>
  );
};

// This function gets called at build and export time to determine
// pages for SSG ("paths", as tokenized array).
export const getStaticPaths: GetStaticPaths = async (context) => {
  // Fallback, along with revalidate in getStaticProps (below),
  // enables Incremental Static Regeneration. This allows us to
  // leave certain (or all) paths empty if desired and static pages
  // will be generated on request (development mode in this example).
  // Alternatively, the entire sitemap could be pre-rendered
  // ahead of time (non-development mode in this example).
  // See https://nextjs.org/docs/basic-features/data-fetching/incremental-static-regeneration

  let paths: StaticPath[] = [];
  let fallback: boolean | 'blocking' = 'blocking';

  if (
    process.env.NODE_ENV !== 'development' &&
    process.env.GENERATE_STATIC_PATHS?.toLowerCase() === 'true'
  ) {
    try {
      // Note: Next.js runs export in production mode
      paths = await client.getPagePaths(
        (sites as SiteInfo[]).map((site) => site.name),
        context?.locales || []
      );
      if (process.env.GENERATE_STATIC_PATHS?.toLowerCase() === 'limited') {
        const lengthBefore = paths.length;
        paths = paths.filter((path) => pathExtractor.extract(path.params).split('/').length <= 3);
        const lengthAfter = paths.length;
        console.log('limited sitemap fetch: paths length: ', lengthBefore, '->', lengthAfter);
      }
    } catch (error) {
      console.error('Error occurred while fetching static paths');
      console.error(error);
    }

    fallback = process.env.EXPORT_MODE ? false : fallback;
  }

  return {
    paths,
    fallback,
  };
};

// This function gets called at build time on server-side.
// It may be called again, on a serverless function, if
// revalidation (or fallback) is enabled and a new request comes in.
export const getStaticProps: GetStaticProps = async (context) => {
  const props = await sitecorePagePropsFactory.create(context);

  const mockError = getMockError(context);

  if (mockError?.throwStaticPropsError) {
    throw Error('mock error getStaticProps');
  }

  const revalidateTimer = !!process.env.ISR_REVALIDATE_TIMER
    ? parseInt(process.env.ISR_REVALIDATE_TIMER)
    : 300;

  // If a redirect is found, return the redirect object with the revalidate timer.
  // During the build phase (static prerendering), Next.js 15 does not allow
  // returning `redirect` from getStaticProps. Return `notFound` with a short
  // revalidate so that the first real request triggers ISR and the redirect is
  // served at runtime instead.
  if (props.redirect) {
    if (process.env.NEXT_PHASE === PHASE_PRODUCTION_BUILD) {
      return {
        notFound: true,
        revalidate: 1,
      };
    }
    return {
      redirect: props.redirect,
      revalidate: revalidateTimer,
    };
  }

  if (!mockError && props.notFound) {
    return {
      notFound: true,
      revalidate: revalidateTimer,
    };
  }

  props.mockError = mockError;
  return {
    props,
    // Next.js will attempt to re-generate the page:
    // - When a request comes in
    // - At most once every 5 seconds
    revalidate: revalidateTimer, // In seconds
    notFound: !mockError && props.notFound, // Returns custom 404 page with a status code of 404 when true
  };
};

export default SitecorePage;
